import { useState, useMemo, useCallback } from 'react';
import { Stack, Group, Text, Badge, ActionIcon, TextInput } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { IconTrash, IconClearAll, IconSearch } from '@tabler/icons-react';
import { alert, toast } from '../../../../components/ui/providers/AlertProvider';
import type { QueueManagementTabProps, QueueStats } from '../types';

export function QueueManagementTab({ queues, onRefresh, onQueueDelete, onQueuePurge }: QueueManagementTabProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredQueues = useMemo(() => {
    if (!searchQuery) return queues;
    const query = searchQuery.toLowerCase();
    return queues.filter((q) => q.queueName.toLowerCase().includes(query));
  }, [queues, searchQuery]);

  const handleDelete = useCallback(
    async (queue: QueueStats) => {
      const confirmed = await alert.confirm(
        'Delete Queue',
        `Are you sure you want to delete queue "${queue.queueName}"?${
          queue.messageCount > 0 ? ` This queue contains ${queue.messageCount} message(s).` : ''
        }`
      );

      if (!confirmed) return;

      await toast.promise(onQueueDelete(queue.queueName), {
        loading: `Deleting queue ${queue.queueName}...`,
        success: `Queue ${queue.queueName} deleted successfully`,
        error: 'Failed to delete queue',
      });

      onRefresh();
    },
    [onQueueDelete, onRefresh]
  );

  const handlePurge = useCallback(
    async (queue: QueueStats) => {
      const confirmed = await alert.confirm(
        'Purge Queue',
        `Are you sure you want to purge all ${queue.messageCount} message(s) from queue "${queue.queueName}"? This action cannot be undone.`
      );

      if (!confirmed) return;

      await toast.promise(onQueuePurge(queue.queueName), {
        loading: `Purging queue ${queue.queueName}...`,
        success: `Queue ${queue.queueName} purged successfully`,
        error: 'Failed to purge queue',
      });

      onRefresh();
    },
    [onQueuePurge, onRefresh]
  );

  const columns: DataTableColumn<QueueStats>[] = useMemo(
    () => [
      {
        key: 'queueName',
        label: 'Queue Name',
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={500}>
            {value}
          </Text>
        ),
      },
      {
        key: 'exchange',
        label: 'Exchange',
        sortable: true,
      },
      {
        key: 'messageCount',
        label: 'Messages',
        sortable: true,
        render: (value) => (
          <Badge color={value > 0 ? 'blue' : 'gray'} variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'consumerCount',
        label: 'Consumers',
        sortable: true,
        render: (value) => (
          <Badge color={value > 0 ? 'green' : 'gray'} variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'isAvailable',
        label: 'Status',
        render: (value) => <Badge color={value ? 'green' : 'red'}>{value ? 'Available' : 'Unavailable'}</Badge>,
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, queue) => (
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="orange"
              onClick={() => handlePurge(queue)}
              disabled={!queue.isAvailable || queue.messageCount === 0}
              aria-label="Purge all messages"
            >
              <IconClearAll size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleDelete(queue)}
              disabled={!queue.isAvailable}
              aria-label="Delete queue"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [handleDelete, handlePurge]
  );

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search queues..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <DataTable columns={columns} data={filteredQueues} striped highlightOnHover />
    </Stack>
  );
}
